import { useState, useEffect } from 'react'
import { ShoppingCart, Calendar, User, Check, X, Eye, Package, History, Trash2 } from 'lucide-react'
import { useSupabaseOrderStore } from '../stores/supabaseOrderStore'
import { useAuthStore } from '../stores/authStore'

export default function OrdersPage() {
  const { 
    orders, 
    fetchOrders, 
    updateOrderStatus,
    deleteOrder 
  } = useSupabaseOrderStore()
  const { isAuthenticated } = useAuthStore()
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [showHistory, setShowHistory] = useState(false)
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Lade Bestellungen beim Initialisieren
  useEffect(() => {
    if (isAuthenticated()) {
      fetchOrders()
    }
  }, [isAuthenticated])

  // Filter und Sortierung der Bestellungen

  const filteredOrders = selectedStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === selectedStatus)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'badge-pending'
      case 'approved': return 'badge-confirmed'
      case 'rejected': return 'badge-cancelled'
      case 'shipped': return 'badge-primary'
      case 'delivered': return 'badge-confirmed'
      default: return 'badge-pending'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Ausstehend'
      case 'approved': return 'Genehmigt'
      case 'rejected': return 'Abgelehnt'
      case 'shipped': return 'Versendet'
      case 'delivered': return 'Geliefert'
      default: return status
    }
  }

  const handleDeleteOrder = async (orderId: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Bestellung unwiderruflich löschen möchten?')) {
      return
    }

    console.log('🗑️ Deleting order:', orderId)
    setDeletingId(orderId)
    
    try {
      const result = await deleteOrder(orderId)
      
      if (result.error) {
        console.error('❌ Delete order error:', result.error)
        alert(`Fehler beim Löschen der Bestellung: ${result.error}`)
        return
      }
      
      console.log('✅ Order deleted successfully')
      alert('Bestellung erfolgreich gelöscht!')
      
    } catch (err: any) {
      console.error('❌ Delete order error:', err)
      alert(`Fehler: ${err.message}`)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              letterSpacing: '-0.025em'
            }}>Bestellungen ({orders.length})</h1>
            <p style={{
              color: '#6b7280',
              fontSize: '18px',
              fontWeight: '500'
            }}>
              Verwalte deine Equipment-Bestellungen
            </p>
          </div>
          <button 
            onClick={() => setShowHistory(!showHistory)}
            style={{
              background: showHistory ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' : 'transparent',
              color: showHistory ? 'white' : '#374151',
              border: showHistory ? 'none' : '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: showHistory ? '0 4px 6px -1px rgba(245, 158, 11, 0.3)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (!showHistory) {
                e.currentTarget.style.borderColor = '#1B7E3A'
                e.currentTarget.style.backgroundColor = '#f0fdf4'
              } else {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(245, 158, 11, 0.4)'
              }
            }}
            onMouseLeave={(e) => {
              if (!showHistory) {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.backgroundColor = 'transparent'
              } else {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(245, 158, 11, 0.3)'
              }
            }}
          >
            <History size={16} />
            {showHistory ? 'Aktuelle anzeigen' : 'Verlauf anzeigen'}
          </button>
        </div>
      </div>

      {/* Filter */}
      {!showHistory && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          padding: '24px',
          marginBottom: '24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative gradient overlay */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '4px',
            background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
            borderRadius: '16px 16px 0 0'
          }} />
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '12px'
          }}>Status filtern</h3>
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            {[
              { key: 'all', label: 'Alle' },
              { key: 'pending', label: 'Ausstehend' },
              { key: 'approved', label: 'Genehmigt' },
              { key: 'rejected', label: 'Abgelehnt' },
              { key: 'shipped', label: 'Versendet' },
              { key: 'delivered', label: 'Geliefert' }
            ].map(status => (
              <button
                key={status.key}
                onClick={() => setSelectedStatus(status.key)}
                style={{
                  background: selectedStatus === status.key 
                    ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                    : 'transparent',
                  color: selectedStatus === status.key ? 'white' : '#374151',
                  border: selectedStatus === status.key ? 'none' : '1px solid #d1d5db',
                  borderRadius: '12px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: selectedStatus === status.key 
                    ? '0 2px 4px rgba(27, 126, 58, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (selectedStatus !== status.key) {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.backgroundColor = '#f0fdf4'
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedStatus !== status.key) {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bestellungen */}
      {filteredOrders.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '64px 32px',
          background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
          borderRadius: '16px',
          border: '1px solid #e5e7eb'
        }}>
          <ShoppingCart size={64} style={{ margin: '0 auto 1rem', color: '#d1d5db' }} />
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '8px',
            color: '#111827'
          }}>Keine Bestellungen</h3>
          <p style={{
            color: '#6b7280'
          }}>
            {selectedStatus === 'all' 
              ? 'Du hast noch keine Bestellungen aufgegeben.' 
              : `Keine Bestellungen mit Status "${getStatusText(selectedStatus)}".`}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {filteredOrders.map(order => (
            <div key={order.id} style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb',
              padding: '24px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative gradient overlay */}
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '4px',
                background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
                borderRadius: '16px 16px 0 0'
              }} />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px'
              }}>
                <div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '8px'
                  }}>Bestellung #{order.id}</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '16px',
                    fontSize: '14px',
                    color: '#6b7280',
                    marginTop: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Calendar size={16} />
                      <span>{new Date(order.orderDate).toLocaleDateString('de-DE')}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <User size={16} />
                      <span>{order.orderedBy}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Package size={16} />
                      <span>{order.team}</span>
                    </div>
                    <div>
                      <span>Lieferung: {order.deliveryMonth}</span>
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    ...(() => {
                      const statusColor = getStatusColor(order.status)
                      switch (statusColor) {
                        case 'badge-pending':
                          return { background: '#fef3c7', color: '#92400e' }
                        case 'badge-confirmed':
                          return { background: '#d1fae5', color: '#065f46' }
                        case 'badge-cancelled':
                          return { background: '#fee2e2', color: '#991b1b' }
                        case 'badge-primary':
                          return { background: '#dbeafe', color: '#1e40af' }
                        default:
                          return { background: '#f3f4f6', color: '#374151' }
                      }
                    })()
                  }}>
                    {getStatusText(order.status)}
                  </span>
                  <button
                    onClick={() => handleDeleteOrder(order.id)}
                    disabled={deletingId === order.id}
                    style={{
                      padding: '8px',
                      backgroundColor: deletingId === order.id ? '#f3f4f6' : '#fee2e2',
                      color: deletingId === order.id ? '#9ca3af' : '#dc2626',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: deletingId === order.id ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (deletingId !== order.id) {
                        e.currentTarget.style.backgroundColor = '#fecaca'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (deletingId !== order.id) {
                        e.currentTarget.style.backgroundColor = '#fee2e2'
                      }
                    }}
                    title="Bestellung löschen"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Artikel-Übersicht */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    fontWeight: '500',
                    color: '#111827'
                  }}>{order.items.length} Artikel</span>
                  <button
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease',
                      color: '#374151'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1B7E3A'
                      e.currentTarget.style.backgroundColor = '#f0fdf4'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db'
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <Eye size={16} />
                    {expandedOrder === order.id ? 'Weniger' : 'Details'}
                  </button>
                </div>

                {expandedOrder === order.id && (
                  <div style={{
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {order.items.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '14px'
                      }}>
                        <span>{item.name} {item.size ? `(${item.size})` : ''}</span>
                        <span>{item.quantity}x</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Trainer und Bemerkungen */}
              <div style={{
                fontSize: '14px',
                color: '#6b7280',
                marginBottom: '16px'
              }}>
                <div><strong>Trainer:</strong> {order.trainerName}</div>
                {order.notes && <div><strong>Bemerkungen:</strong> {order.notes}</div>}
              </div>

              {/* Genehmigungsinfo */}
              {order.approverName && (
                <div style={{
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '14px' }}>
                    <div><strong>Genehmigt von:</strong> {order.approverName}</div>
                    {order.approvalDate && (
                      <div><strong>Genehmigungsdatum:</strong> {new Date(order.approvalDate).toLocaleDateString('de-DE')}</div>
                    )}
                    {order.approvalComment && (
                      <div><strong>Kommentar:</strong> {order.approvalComment}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Admin-Aktionen */}
              {order.status === 'pending' && (
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => updateOrderStatus(order.id, 'approved', { 
                      approverName: 'Admin', 
                      approvalComment: 'Automatisch genehmigt' 
                    })}
                    style={{
                      background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '12px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      flex: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                    }}
                  >
                    <Check size={16} />
                    Genehmigen
                  </button>
                  <button
                    onClick={() => updateOrderStatus(order.id, 'rejected', { 
                      approverName: 'Admin', 
                      approvalComment: 'Abgelehnt' 
                    })}
                    style={{
                      background: 'transparent',
                      color: '#dc2626',
                      border: '1px solid #dc2626',
                      borderRadius: '12px',
                      padding: '12px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      flex: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <X size={16} />
                    Ablehnen
                  </button>
                </div>
              )}

              {order.status === 'approved' && (
                <button
                  onClick={() => updateOrderStatus(order.id, 'shipped')}
                  style={{
                    background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                  }}
                >
                  Als versendet markieren
                </button>
              )}

              {order.status === 'shipped' && (
                <button
                  onClick={() => updateOrderStatus(order.id, 'delivered')}
                  style={{
                    background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                  }}
                >
                  Als geliefert markieren
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* History View */}
      {showHistory && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <History style={{ width: '24px', height: '24px', color: '#2563eb' }} />
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827'
            }}>Equipment-Bestellungen Verlauf</h2>
          </div>

          {/* Approved/Completed Orders */}
          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#065f46',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Check style={{ width: '20px', height: '20px' }} />
              Genehmigte/Abgeschlossene Bestellungen ({orders.filter(o => o.status === 'approved' || o.status === 'shipped' || o.status === 'delivered').length})
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {orders.filter(o => o.status === 'approved' || o.status === 'shipped' || o.status === 'delivered').length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '32px',
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  borderRadius: '12px'
                }}>
                  <Check style={{ width: '48px', height: '48px', color: '#4ade80', margin: '0 auto 8px' }} />
                  <p style={{ color: '#16a34a' }}>Keine genehmigten/abgeschlossenen Bestellungen</p>
                </div>
              ) : (
                orders.filter(o => o.status === 'approved' || o.status === 'shipped' || o.status === 'delivered').map(order => (
                  <div key={order.id} style={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '1px solid #bbf7d0',
                    borderRadius: '12px',
                    padding: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px'
                    }}>
                      <div>
                        <h4 style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#064e3b',
                          marginBottom: '8px'
                        }}>Bestellung #{order.id.slice(-8)}</h4>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: '16px',
                          fontSize: '14px',
                          color: '#15803d',
                          marginTop: '8px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <Calendar style={{ width: '16px', height: '16px' }} />
                            <span>{new Date(order.orderDate).toLocaleDateString('de-CH')}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <User style={{ width: '16px', height: '16px' }} />
                            <span>{order.orderedBy}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <Package style={{ width: '16px', height: '16px' }} />
                            <span>{order.team}</span>
                          </div>
                          <div>
                            <span>Lieferung: {order.deliveryMonth}</span>
                          </div>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          ...(() => {
                            if (order.status === 'delivered') {
                              return { background: '#dbeafe', color: '#1e40af' }
                            } else if (order.status === 'shipped') {
                              return { background: '#e9d5ff', color: '#7c3aed' }
                            } else {
                              return { background: '#dcfce7', color: '#166534' }
                            }
                          })()
                        }}>
                          {order.status === 'delivered' ? 'Geliefert' :
                           order.status === 'shipped' ? 'Versendet' :
                           'Genehmigt'}
                        </span>
                        <button
                          onClick={() => handleDeleteOrder(order.id)}
                          disabled={deletingId === order.id}
                          style={{
                            padding: '8px',
                            backgroundColor: deletingId === order.id ? '#f3f4f6' : '#fee2e2',
                            color: deletingId === order.id ? '#9ca3af' : '#dc2626',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: deletingId === order.id ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            if (deletingId !== order.id) {
                              e.currentTarget.style.backgroundColor = '#fecaca'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (deletingId !== order.id) {
                              e.currentTarget.style.backgroundColor = '#fee2e2'
                            }
                          }}
                          title="Bestellung löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <div style={{
                      fontSize: '14px',
                      color: '#15803d'
                    }}>
                      <span style={{ fontWeight: '500' }}>{order.items.length} Artikel bestellt</span>
                      <span style={{ margin: '0 8px' }}>•</span>
                      <span>CHF {order.items.reduce((total, item) => total + (item.price || 0) * item.quantity, 0).toFixed(2)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Rejected Orders */}
          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#991b1b',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <X style={{ width: '20px', height: '20px' }} />
              Abgelehnte Bestellungen ({orders.filter(o => o.status === 'rejected').length})
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {orders.filter(o => o.status === 'rejected').length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '32px',
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                  borderRadius: '12px'
                }}>
                  <X style={{ width: '48px', height: '48px', color: '#f87171', margin: '0 auto 8px' }} />
                  <p style={{ color: '#dc2626' }}>Keine abgelehnten Bestellungen</p>
                </div>
              ) : (
                orders.filter(o => o.status === 'rejected').map(order => (
                  <div key={order.id} style={{
                    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                    border: '1px solid #fca5a5',
                    borderRadius: '12px',
                    padding: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px'
                    }}>
                      <div>
                        <h4 style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#7f1d1d',
                          marginBottom: '8px'
                        }}>Bestellung #{order.id.slice(-8)}</h4>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: '16px',
                          fontSize: '14px',
                          color: '#b91c1c',
                          marginTop: '8px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <Calendar style={{ width: '16px', height: '16px' }} />
                            <span>{new Date(order.orderDate).toLocaleDateString('de-CH')}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <User style={{ width: '16px', height: '16px' }} />
                            <span>{order.orderedBy}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <Package style={{ width: '16px', height: '16px' }} />
                            <span>{order.team}</span>
                          </div>
                          <div>
                            <span>Lieferung: {order.deliveryMonth}</span>
                          </div>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{
                          padding: '8px 16px',
                          background: '#fee2e2',
                          color: '#991b1b',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          Abgelehnt
                        </span>
                        <button
                          onClick={() => handleDeleteOrder(order.id)}
                          disabled={deletingId === order.id}
                          style={{
                            padding: '8px',
                            backgroundColor: deletingId === order.id ? '#f3f4f6' : '#fee2e2',
                            color: deletingId === order.id ? '#9ca3af' : '#dc2626',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: deletingId === order.id ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            if (deletingId !== order.id) {
                              e.currentTarget.style.backgroundColor = '#fecaca'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (deletingId !== order.id) {
                              e.currentTarget.style.backgroundColor = '#fee2e2'
                            }
                          }}
                          title="Bestellung löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <div style={{
                      fontSize: '14px',
                      color: '#b91c1c'
                    }}>
                      <span style={{ fontWeight: '500' }}>{order.items.length} Artikel</span>
                      {order.notes && (
                        <>
                          <span style={{ margin: '0 8px' }}>•</span>
                          <span>Grund: {order.notes}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
